import React from 'react';
import { Link } from 'react-router-dom';
import { Image } from 'antd';
import { CarDto as Car, CarDtoStatusEnum as CarStatusEnum } from '../../apiClient';
import { UUID } from 'crypto';

// Add CardSize enum to control the size of the card
export enum CardSize {
  Small = 'small',
  Big = 'big'
}

interface CarCardProps {
  car: Car;
  selectedItems: UUID[];
  onHeartClick: (id: UUID) => void;
  size?: CardSize; // Optional size property with CardSize enum
}

const CarCard: React.FC<CarCardProps> = ({ 
  car, 
  selectedItems, 
  onHeartClick, 
  size = CardSize.Small // Default to small if not specified
}) => {
  // Determine the CSS class based on size
  const cardSizeClass = size === CardSize.Big
    ? "col-xl-6 col-lg-6 col-md-6 col-12"
    : "col-xl-6 col-lg-6 col-md-6 col-12";

    const isSold = car.status === CarStatusEnum.Sold;
    const hasDiscount = car.discountAmount && car.discountAmount > 0;
    
    // Determine the image to display
    // Priority: Primary Image -> First Image -> Legacy Image -> Placeholder
    const primaryImage = car.images?.find(img => img.isPrimary);
    const firstImage = car.images && car.images.length > 0 ? car.images[0] : null;
    const activeImage = primaryImage || firstImage;

    let displayImage = "assets/img/cars/car-01.jpg";

    if (activeImage && activeImage.id) {
        // Construct API URL
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        displayImage = `${apiUrl}/api/v1/public/car/image/${activeImage.id}`;
    } else if (car.image) {
        // Fallback to legacy string URL
         if (car.image.startsWith('http')) {
             displayImage = car.image;
         } else {
             displayImage = car.image;
         }
    }

  return (
    <div className={cardSizeClass} data-aos="fade-down">
      <div className={`listing-item ${isSold ? 'sold-out' : ''}`}>
        <div className="listing-img">
          <Link to={`/listing-details/${car.id}`} style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
            <Image 
              src={displayImage} 
              className="img-fluid" 
              alt={car.make} 
              style={{ width: '100%', height: '250px', objectFit: 'cover', backgroundColor: 'white'}}
              preview={false} 
            />
            {isSold && (
                <div className="sold-badge" style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    background: 'rgba(255, 0, 0, 0.8)', color: 'white', padding: '10px 20px',
                    fontSize: '24px', fontWeight: 'bold', borderRadius: '5px', zIndex: 10
                }}>
                    VÂNDUT
                </div>
            )}
            {hasDiscount && !isSold && (
                <div className="discount-badge" style={{
                    position: 'absolute', top: '10px', right: '10px',
                    background: '#e63946', color: 'white', padding: '5px 10px',
                    fontSize: '14px', fontWeight: 'bold', borderRadius: '3px', zIndex: 5
                }}>
                    Economisești €{car.discountAmount}
                </div>
            )}
          </Link>
          <div className="fav-item" onClick={(e) => {
            e.stopPropagation();
            onHeartClick(car.id as unknown as UUID);
          }}>
            <span className="featured-text">{car.make} {car.model}</span>
          </div>
        </div>
        <div className="listing-content">
          <div className="listing-features">
            <h3 className="listing-title">
              <Link to={`/listing-details/${car.id}`}>{car.make} {car.model} {car.features?.year}</Link>
            </h3>
          </div>
          <div className="listing-details-group">
            <div className="row mt-3">
              <div className="col-md-4 col-sm-4 col-6 d-flex align-items-center gap-2 mb-2">
                 <i className="feather icon-droplet" />
                 <p className="mb-0 text-truncate" title={car.features?.fuelType}>{car.features?.fuelType}</p>
              </div>
              <div className="col-md-4 col-sm-4 col-6 d-flex align-items-center gap-2 mb-2">
                 <i className="feather icon-settings" />
                 <p className="mb-0 text-truncate" title={car.transmission}>{car.transmission}</p>
              </div>
              <div className="col-md-4 col-sm-4 col-6 d-flex align-items-center gap-2 mb-2">
                 <i className="feather icon-activity" />
                 <p className="mb-0 text-truncate" title={`${car.features?.mileage} km`}>{car.features?.mileage} km</p>
              </div>
              <div className="col-md-4 col-sm-4 col-6 d-flex align-items-center gap-2">
                 <i className="feather icon-zap" />
                 <p className="mb-0 text-truncate" title={`${car.features?.power} CP`}>{car.features?.power} CP</p>
              </div>
              <div className="col-md-4 col-sm-4 col-6 d-flex align-items-center gap-2">
                 <i className="feather icon-layers" />
                 <p className="mb-0 text-truncate" title={`${car.features?.engineSize} cm³`}>{car.features?.engineSize} cm³</p>
              </div>
              <div className="col-md-4 col-sm-4 col-6 d-flex align-items-center gap-2">
                 <i className="feather icon-info" />
                 <p className="mb-0">
                   <span className={`vat-regime-badge-small ${car.vatRegime === 'MARGIN' ? 'margin' : 'full'}`}>
                     {car.vatRegime === 'MARGIN' ? 'TVA Marjă' : 'TVA deductibil'}
                   </span>
                 </p>
              </div>
            </div>
          </div>
          <div className="listing-location-details mt-3">
            <div className="listing-price w-100 d-flex justify-content-between align-items-center">
              <div>
                {hasDiscount && !isSold ? (
                    <>
                        <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '14px', marginRight: '8px' }}>
                            €{((car.price || 0) + (car.vatAmount || 0)).toLocaleString()}
                        </span>
                        <h6 style={{ color: '#e63946' }}>€{car.priceWithVat?.toLocaleString()}</h6>
                    </>
                ) : (
                    <h6>€{car.priceWithVat?.toLocaleString()}</h6>
                )}
              </div>
              <div className="d-flex gap-2">
                <a href="tel:+40748123456" className="btn btn-success btn-sm">
                  <i className="feather icon-phone me-1" /> Sună
                </a>
                <Link to={`/listing-details/${car.id}`} className="btn btn-primary btn-sm">
                  Vezi Ofertă <i className="feather icon-arrow-right ms-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarCard;
